import React, { useRef, useEffect } from 'react';
import { Field, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';

const MAX_FILE_SIZE_BYTES = 256 * 1024 * 1024;
const MAX_FILE_SIZE_MB = MAX_FILE_SIZE_BYTES / (1024 * 1024);

const useStyles = makeStyles({
  container: {
    marginBottom: tokens.spacingVerticalXL,
    maxWidth: '100%',
  },

  input: {
    display: 'block',
    marginBottom: '25px',
    padding: tokens.spacingVerticalM,
    width: '100%',
    border: `2px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    fontSize: tokens.fontSizeBase400,
    backgroundColor: tokens.colorNeutralBackground3,
    cursor: 'pointer',
    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
    opacity: 1,
    transitionProperty: 'opacity',
    transitionDuration: '0.2s',
    transitionTimingFunction: 'ease',
  },
  inputDisabled: {
    backgroundColor: tokens.colorNeutralBackground3,
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  fileList: {
    padding: tokens.spacingVerticalL,
    backgroundColor: tokens.colorBrandBackground2,
    border: `1px solid ${tokens.colorBrandForeground1}`,
    borderRadius: tokens.borderRadiusSmall,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorBrandForeground1,
    marginTop: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalXL,
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  fileListTitle: {
    fontWeight: 'bold',
    marginBottom: tokens.spacingVerticalS,
  },
  fileItem: {
    marginBottom: tokens.spacingVerticalXS,
    wordBreak: 'break-all',
  },
  helperText: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    marginTop: tokens.spacingVerticalXS,
    marginBottom: tokens.spacingVerticalM,
  },
});

interface FileUploadProps {
  files: File[];
  isProcessing: boolean;
  onFilesChange: (files: File[], errorMessage?: string) => void;
}

export default function FileUpload({ files, isProcessing, onFilesChange }: FileUploadProps) {
  const styles = useStyles();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset the file input when files array is cleared
  useEffect(() => {
    if (files.length === 0 && fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [files]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);

    if (selectedFiles.length > 1) {
      onFilesChange([], 'Select one .jsonl file. Batch uploads create one job per file.');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Validate all files are .jsonl
    const invalidFiles = selectedFiles.filter(file => !file.name.endsWith('.jsonl'));
    if (invalidFiles.length > 0) {
      onFilesChange([], 'The selected file must be .jsonl format.');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    const oversizedFiles = selectedFiles.filter(file => file.size > MAX_FILE_SIZE_BYTES);
    if (oversizedFiles.length > 0) {
      onFilesChange([], `File size exceeds the ${MAX_FILE_SIZE_MB} MB limit: ${oversizedFiles.map(f => f.name).join(', ')}`);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    onFilesChange(selectedFiles);
  };

  return (
    <div className={styles.container}>
      <Field label="JSONL file" required>
        <input
          id="file-upload"
          type="file"
          accept=".jsonl"
          ref={fileInputRef}
          onChange={handleFileChange}
          disabled={isProcessing}
          className={mergeClasses(styles.input, isProcessing && styles.inputDisabled)}
        />
      </Field>
      <div className={styles.helperText}>
        Upload one JSONL file per batch job. Max file size: {MAX_FILE_SIZE_MB} MB. Max row size: approximately 256 KB
        because each row is sent as one Azure Service Bus Standard message; the usable payload is slightly lower after
        message overhead. There is no row count limit.
      </div>

      {files.length > 0 && (
        <div className={styles.fileList}>
          <div className={styles.fileListTitle}>
            Selected file:
          </div>
          {files.map((file, index) => (
            <div key={index} className={styles.fileItem}>
              • <strong>{file.name}</strong> ({(file.size / 1024).toFixed(1)} KB)
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
